import { useState, useEffect } from 'react';
import { X, Heart, Star, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { toast } from 'sonner';
import { API_URL } from '../lib/api-client';
import { logger } from '../utils/logger';

interface SuperInterestPopupProps {
  onViewProfile: (profile: any) => void;
}

interface SuperInterest {
  _id: string;
  fromProfile: {
    _id: string;
    firstName: string;
    lastName?: string;
    photos?: string[];
    age?: number;
    city?: string;
    state?: string;
  };
  createdAt: string;
}

export const SuperInterestPopup = ({ onViewProfile }: SuperInterestPopupProps) => {
  const [currentSuperInterest, setCurrentSuperInterest] = useState<SuperInterest | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);

  // Fetch pending super interests on mount
  useEffect(() => {
    // Wait 2 seconds after mount to show modal (let page load first)
    const timer = setTimeout(() => {
      fetchPendingSuperInterests();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const fetchPendingSuperInterests = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      logger.log('🔍 Fetching pending super interests...');

      const response = await fetch(`${API_URL}/interactions/received?type=superlike`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        logger.log('📥 Received super interests:', data);
        
        // Backend returns data.interactions array
        const interactions = data.data?.interactions || [];
        
        if (interactions.length > 0) {
          // Get viewed super interest IDs from localStorage (track by interaction._id, NOT profile ID)
          const viewedInteractionIds = JSON.parse(localStorage.getItem('viewedSuperInteractionIds') || '[]');
          logger.log('👁️ Already viewed interaction IDs:', viewedInteractionIds);
          
          // Transform interactions to use fromProfile instead of fromUserProfile
          const transformedInteractions = interactions.map((interaction: any) => ({
            ...interaction,
            fromProfile: interaction.fromUserProfile || interaction.fromProfile
          }));
          
          // Find first super interest that hasn't been shown (by interaction ID)
          const unviewedInterest = transformedInteractions.find((interest: any) => 
            interest.fromProfile && !viewedInteractionIds.includes(interest._id)
          );

          if (unviewedInterest) {
            logger.log('⭐ Found unviewed super interest:', unviewedInterest);
            setCurrentSuperInterest(unviewedInterest);
            setIsVisible(true);
          } else {
            logger.log('✅ All super interests already viewed');
          }
        } else {
          logger.log('📭 No super interests found');
        }
      }
    } catch (error) {
      logger.error('Failed to fetch pending super interests:', error);
    }
  };

  const markAsViewed = (interactionId: string) => {
    const viewedInteractionIds = JSON.parse(localStorage.getItem('viewedSuperInteractionIds') || '[]');
    if (!viewedInteractionIds.includes(interactionId)) {
      viewedInteractionIds.push(interactionId);
      localStorage.setItem('viewedSuperInteractionIds', JSON.stringify(viewedInteractionIds));
      logger.log('✅ Marked interaction as viewed:', interactionId);
    }
  };

  const handleAccept = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!currentSuperInterest) return;

    setIsAccepting(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Accept the super interest instead of sending a new interest
      const response = await fetch(`${API_URL}/interactions/accept/${currentSuperInterest._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success("Interest Accepted! 💕", {
          description: 'You can now start chatting!',
          duration: 5000
        });
      }
    } catch (error) {
      toast.error('Failed to accept interest');
    } finally {
      // Always mark as viewed and close modal, even if API fails
      markAsViewed(currentSuperInterest._id);
      setIsVisible(false);
      setCurrentSuperInterest(null);
      
      // Check for next super interest after a delay
      setTimeout(() => {
        fetchPendingSuperInterests();
      }, 1000);
    }

    setIsAccepting(false);
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!currentSuperInterest) return;

    setIsDismissing(true);

    markAsViewed(currentSuperInterest._id);
    setIsVisible(false);
    setCurrentSuperInterest(null);
    toast.success('Super Interest dismissed');
    
    // Check for next super interest after a delay
    setTimeout(() => {
      fetchPendingSuperInterests();
    }, 1000);

    setIsDismissing(false);
  };

  const handleRemindLater = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!currentSuperInterest) return;

    // Mark this user's super like as viewed
    markAsViewed(currentSuperInterest._id);
    setIsVisible(false);
    setCurrentSuperInterest(null);
    
    // Check for next super interest after a delay
    setTimeout(() => {
      fetchPendingSuperInterests();
    }, 1000);
  };

  const handleBackdropClick = () => {
    if (!currentSuperInterest) return;
    
    // Mark this user's super like as viewed
    markAsViewed(currentSuperInterest._id);
    setIsVisible(false);
    setCurrentSuperInterest(null);
    
    // Check for next super interest after a delay
    setTimeout(() => {
      fetchPendingSuperInterests();
    }, 1000);
  };

  const handleViewProfile = () => {
    if (!currentSuperInterest) return;
    
    // Mark this user's super like as viewed
    markAsViewed(currentSuperInterest._id);
    setIsVisible(false);
    setCurrentSuperInterest(null);
    onViewProfile(currentSuperInterest.fromProfile);
    
    // Check for next super interest after a delay
    setTimeout(() => {
      fetchPendingSuperInterests();
    }, 1000);
  };

  const getProfilePhoto = (profile: any) => {
    if (profile?.photos?.length > 0) {
      if (typeof profile.photos[0] === 'object' && profile.photos[0]?.url) {
        return profile.photos[0].url;
      }
      if (typeof profile.photos[0] === 'string') {
        if (profile.photos[0].startsWith('http')) {
          return profile.photos[0];
        }
        return `${API_URL.replace('/api', '')}/uploads/${profile.photos[0]}`;
      }
    }
    
    return null;
  };

  const calculateAge = (dateOfBirth: string) => {
    if (!dateOfBirth) return null;
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (!isVisible || !currentSuperInterest) return null;

  const photo = getProfilePhoto(currentSuperInterest.fromProfile);
  const age = currentSuperInterest.fromProfile.age || calculateAge((currentSuperInterest.fromProfile as any).dateOfBirth);
  const initials = `${currentSuperInterest.fromProfile.firstName?.charAt(0) || ''}${currentSuperInterest.fromProfile.lastName?.charAt(0) || ''}`.toUpperCase();

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        onClick={handleBackdropClick}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden pointer-events-auto animate-in zoom-in-95 duration-300 cursor-pointer"
          onClick={handleViewProfile}
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-amber-400 via-orange-500 to-pink-500 p-6 text-white">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleReject(e);
              }}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            >
              <X className="size-6" />
            </button>
            
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-2">
                <Star className="size-6 fill-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Super Interest!</h2>
                <p className="text-white/90 text-sm">Someone is really interested in you</p>
              </div>
            </div>
          </div>

          {/* Profile Photo */}
          <div className="relative">
            <div className="aspect-[4/5] bg-gradient-to-br from-pink-100 to-purple-100">
              {photo ? (
                <ImageWithFallback
                  src={photo}
                  alt={currentSuperInterest.fromProfile.firstName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-6xl text-gray-400">{initials}</span>
                </div>
              )}
            </div>
            
            {/* Floating badge */}
            <div className="absolute top-4 left-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
              <Star className="size-4 fill-white" />
              <span className="font-semibold">Super Interest</span>
            </div>
          </div>

          {/* Profile Info */}
          <div className="p-6">
            <h3 className="text-2xl font-bold mb-1">
              {currentSuperInterest.fromProfile.firstName} {currentSuperInterest.fromProfile.lastName || ''}
            </h3>
            <p className="text-gray-600 mb-4">
              {age && `${age} yrs`}
              {age && currentSuperInterest.fromProfile.city && ' • '}
              {currentSuperInterest.fromProfile.city}
            </p>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleRemindLater}
                variant="outline"
                className="flex-1 border-gray-300 hover:bg-gray-50"
                disabled={isDismissing || isAccepting}
              >
                {isDismissing ? <Loader2 className="size-4 mr-2 animate-spin" /> : <X className="size-4 mr-2" />}
                Not Interested
              </Button>
              <Button
                onClick={handleAccept}
                className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
                disabled={isAccepting || isDismissing}
              >
                {isAccepting ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Heart className="size-4 mr-2" />}
                Accept
              </Button>
            </div>

            <p className="text-center text-xs text-gray-500 mt-4">
              Tap anywhere on the card to view full profile
            </p>
          </div>
        </div>
      </div>
    </>
  );
}